import React from 'react'
import { Card, CardMedia, Typography, Button, Box } from '@mui/material'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { makeStyles } from 'tss-react/mui'

import useModals from 'src/Hooks/useModals'
import { useState } from 'react'
import { useTypedSelector } from 'src/State/RootReducer'
import { removeRetrofitBattery } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import { useDispatch } from 'react-redux'
import { RetrofitBatteryModalResponse } from 'src/Libs/shop/models'
import { ShopMainSkeleton } from 'src/Libs/shop/components/Skeletons'
import useShopSystemConfiguration from '../../hooks/use-shop-system-configuration'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { currencyFormatter } from '../../utils/formatter'
import { getImageUrlBySize } from '../../utils'
import { ShopImageThumnbailSizes } from '../../types'
import { useEffectShopAccess } from '../../hooks'

interface MainComponentProps {
  handleAllowContinueMain?: (allow: boolean) => void
}

const useStyles = makeStyles()(theme => ({
  systemConfiguration: {
    marginBottom: '12px',

    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  },
  paperContent: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingRight: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflowX: 'hidden',
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
      paddingRight: 0,
    },
  },
  product: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '265px',
    maxWidth: '225px',
    flexShrink: 0,
    margin: '12px',
    padding: 0,
    paddingBottom: '12px',
    position: 'relative',
  },
  productActive: {
    border: `1.5px solid ${theme.palette.primary.main}`,
    boxShadow: 'none',
  },
  activeLabel: {
    top: theme.spacing(2),
    left: theme.spacing(2),
    position: 'absolute',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderRadius: '24px',
  },
  productImage: {
    margin: 0,
    padding: 0,
    objectFit: 'fill',
    width: '100%',
    maxWidth: '100%',
    height: '220px',
    marginBottom: '12px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  buttonContainer: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  button: {
    fontSize: '16px',
    padding: '16px 0px',
  },
  gridContainer: {
    display: 'flex',
    overflowX: 'auto',
    width: '100%',
    minWidth: '30px',
    flexWrap: 'nowrap',
    marginLeft: '12px',
  },
  priceContainer: {
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  flexPrice: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  containerTextWrapper: {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
}))

/* The length is default set to 1 since this component shows up only whne there is 1 battery, and there cannot be more than 1 */
const RETROFIT_BATTERY_LENGTH = 1

/*
 * This Component shows Retrofit Batteries and add the Selected one into the Cart.
 */

const RetrofitBatteryCategory: React.VFC<MainComponentProps> = ({ handleAllowContinueMain }) => {
  const { t } = useCustomTranslation()
  const [productData, setProductData] = useState<RetrofitBatteryModalResponse | null>(null)
  const configuratorItems = useTypedSelector(state => state.systemStorage.items)
  const retrofitBattery = configuratorItems.length > 0 ? configuratorItems[0].configuration.retrofitBattery : null
  const dispatch = useDispatch()
  const { getRetrofitBattery } = useShopSystemConfiguration()
  const [isLoading, setIsLoading] = useState(true)

  const { classes, cx } = useStyles()
  const { openShopMoreInfo } = useModals()

  const productImageUrl = getImageUrlBySize(productData?.product?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Small)

  const unitPrice = productData?.product.calculatedPrice.unitPrice ?? 0
  const price = productData?.product?.calculatedPrice?.listPrice?.price ?? 0

  useEffectShopAccess(() => {
    if (retrofitBattery !== null) {
      setIsLoading(true)
      getRetrofitBattery(retrofitBattery.id)
        .then(result => {
          setProductData(result)
        })
        .catch(error => console.log(error))
        .finally(() => {
          setIsLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrofitBattery])

  if (isLoading) return <ShopMainSkeleton shopSkeletonType="product" />

  return (
    <Box sx={{ width: '100%', marginRight: 0 }}>
      <Box className={classes.systemConfiguration}>
        <Paper title={`${t('shop.retrofit-battery.title')} (${RETROFIT_BATTERY_LENGTH})`} className={classes.paperContent}>
          {productData && (
            <Box className={classes.gridContainer}>
              <Card className={cx(classes.product, classes.productActive)}>
                <Typography className={classes.activeLabel}>{t('shop.common.selected')}</Typography>
                <CardMedia
                  className={classes.productImage}
                  component="img"
                  image={productImageUrl}
                  alt="Product"
                  onClick={() => {
                    openShopMoreInfo({ productId: productData.product.id })
                  }}
                />
                <Box className={classes.containerTextWrapper}>
                  <Typography align="left" variant="h3">
                    {productData.product?.translated && productData.product.translated.name}
                  </Typography>
                  <Box className={classes.priceContainer}>
                    <Box className={classes.flexPrice}>
                      <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
                        {unitPrice ? currencyFormatter(unitPrice) : currencyFormatter(price)}
                      </Typography>
                      {unitPrice < price && (
                        <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                          {currencyFormatter(price)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box className={classes.buttonContainer}>
                  <Button
                    className={classes.button}
                    size="large"
                    color="inherit"
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      openShopMoreInfo({ productId: productData.product.id })
                    }}
                  >
                    {t('shop.common.more-info')}
                  </Button>
                  <Button
                    className={classes.button}
                    color="inherit"
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      retrofitBattery != null && dispatch(removeRetrofitBattery(retrofitBattery.id))
                    }}
                  >
                    {t('shop.common.remove')}
                  </Button>
                </Box>
              </Card>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  )
}

export default RetrofitBatteryCategory
